/**
 * Organization Setup Wizard Page
 * 
 * Multi-step wizard for creating a new organization:
 * 1. Organization details (name, Y-tunnus, address, type)
 * 2. Chart of accounts selection
 * 3. Fiscal period setup
 * 4. Admin user
 * 5. Summary and confirmation
 */

import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { useAuthStore } from '../store/auth'
import { apiClient } from '../api/client'
import StepIndicator from '../components/setup/StepIndicator'
import Step1OrganizationDetails from '../components/setup/Step1OrganizationDetails'
import Step2ChartOfAccounts from '../components/setup/Step2ChartOfAccounts'
import Step3FiscalPeriod from '../components/setup/Step3FiscalPeriod'
import Step4AdminUser from '../components/setup/Step4AdminUser'
import Step5Summary from '../components/setup/Step5Summary'

export interface SetupState {
  setupId: string | null
  organizationId: string | null
  currentStep: number
  step1Data: OrganizationDetailsData | null
  step2Data: ChartOfAccountsData | null
  step3Data: FiscalPeriodData | null
  step4Data: AdminUserData | null
}

export interface OrganizationDetailsData {
  name: string
  business_id?: string
  address?: string
  postal_code?: string
  city?: string
  email?: string
  phone?: string
  website?: string
  type: 'association' | 'company' | 'foundation' | 'other'
  description?: string
}

export interface ChartOfAccountsData {
  template: 'association_basic' | 'association_extended' | 'company' | 'custom'
  custom_accounts?: Array<{
    code: string
    name: string
    account_type: 'asset' | 'liability' | 'equity' | 'income' | 'expense'
  }>
}

export interface FiscalPeriodData {
  start_date: string
  end_date: string
  name?: string
  period_id?: string
}

export interface AdminUserData {
  email: string
  name?: string
  invite_only: boolean
}

export default function SetupWizardPage() {
  const { t } = useTranslation()

  const STEPS = [
    { number: 1, title: t('setup.step1Title'), description: t('setup.step1Description') },
    { number: 2, title: t('setup.step2Title'), description: t('setup.step2Description') },
    { number: 3, title: t('setup.step3Title'), description: t('setup.step3Description') },
    { number: 4, title: t('setup.step4Title'), description: t('setup.step4Description') },
    { number: 5, title: t('setup.step5Title'), description: t('setup.step5Description') },
  ]
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [setupState, setSetupState] = useState<SetupState>({
    setupId: null,
    organizationId: null,
    currentStep: 1,
    step1Data: null,
    step2Data: null,
    step3Data: null,
    step4Data: null,
  })

  // Start setup on mount
  useEffect(() => {
    startSetup()
  }, [])

  const startSetup = async () => {
    try {
      setLoading(true)
      const response = await apiClient.post<{
        success: boolean
        setup_id: string
        organization_id: string
        current_step: number
      }>('/setup/start', {})

      setSetupState({
        setupId: response.setup_id,
        organizationId: response.organization_id,
        currentStep: response.current_step,
        step1Data: null,
        step2Data: null,
        step3Data: null,
        step4Data: null,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to start setup')
    } finally {
      setLoading(false)
    }
  }

  const handleStep1Complete = async (data: OrganizationDetailsData) => {
    if (!setupState.setupId) return

    try {
      setLoading(true)
      const response = await apiClient.put<{
        success: boolean
        current_step: number
      }>(`/setup/${setupState.setupId}/step/1`, data)

      setSetupState(prev => ({
        ...prev,
        currentStep: response.current_step,
        step1Data: data,
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save organization details')
    } finally {
      setLoading(false)
    }
  }

  const handleStep2Complete = async (data: ChartOfAccountsData) => {
    if (!setupState.setupId) return

    try {
      setLoading(true)
      const response = await apiClient.put<{
        success: boolean
        current_step: number
      }>(`/setup/${setupState.setupId}/step/2`, data)

      setSetupState(prev => ({
        ...prev,
        currentStep: response.current_step,
        step2Data: data,
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save chart of accounts')
    } finally {
      setLoading(false)
    }
  }

  const handleStep3Complete = async (data: FiscalPeriodData) => {
    if (!setupState.setupId) return

    try {
      setLoading(true)
      const response = await apiClient.put<{
        success: boolean
        current_step: number
        period_id: string
      }>(`/setup/${setupState.setupId}/step/3`, data)

      setSetupState(prev => ({
        ...prev,
        currentStep: response.current_step,
        step3Data: { ...data, period_id: response.period_id },
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save fiscal period')
    } finally {
      setLoading(false)
    }
  }

  const handleStep4Complete = async (data: AdminUserData) => {
    if (!setupState.setupId) return

    try {
      setLoading(true)
      const response = await apiClient.put<{
        success: boolean
        current_step: number
      }>(`/setup/${setupState.setupId}/step/4`, data)

      setSetupState(prev => ({
        ...prev,
        currentStep: response.current_step,
        step4Data: data,
      }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save admin user')
    } finally {
      setLoading(false)
    }
  }

  const handleComplete = async () => {
    if (!setupState.setupId) return

    try {
      setLoading(true)
      await apiClient.post<{
        success: boolean
        organization_id: string
      }>(`/setup/${setupState.setupId}/complete`, {})

      // Redirect to dashboard
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to complete setup')
      setLoading(false)
    }
  }

  const handleCancel = async () => {
    if (!setupState.setupId) return

    if (!confirm(t('setup.cancelConfirm'))) {
      return
    }

    try {
      await apiClient.delete(`/setup/${setupState.setupId}`)
      navigate('/dashboard')
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to cancel setup')
    }
  }

  const goToStep = (step: number) => {
    // Only allow going to completed steps or current step
    if (step < setupState.currentStep) {
      setSetupState(prev => ({ ...prev, currentStep: step }))
    }
  }

  if (loading && !setupState.setupId) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Aloitetaan...</p>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Luo uusi organisaatio</h1>
          <p className="text-gray-600">Käydään läpi muutama vaihe yhdistyksesi perustamiseksi</p>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
            <div className="flex items-center justify-between">
              <span>{error}</span>
              <button
                onClick={() => setError(null)}
                className="text-red-500 hover:text-red-700"
              >
                ×
              </button>
            </div>
          </div>
        )}

        {/* Step indicator */}
        <StepIndicator
          steps={STEPS}
          currentStep={setupState.currentStep}
          onStepClick={goToStep}
        />

        {/* Step content */}
        <div className="mt-8">
          {setupState.currentStep === 1 && (
            <Step1OrganizationDetails
              initialData={setupState.step1Data}
              onSubmit={handleStep1Complete}
              onCancel={handleCancel}
              loading={loading}
            />
          )}

          {setupState.currentStep === 2 && (
            <Step2ChartOfAccounts
              initialData={setupState.step2Data}
              onSubmit={handleStep2Complete}
              onBack={() => goToStep(1)}
              loading={loading}
            />
          )}

          {setupState.currentStep === 3 && (
            <Step3FiscalPeriod
              initialData={setupState.step3Data}
              onSubmit={handleStep3Complete}
              onBack={() => goToStep(2)}
              loading={loading}
            />
          )}

          {setupState.currentStep === 4 && (
            <Step4AdminUser
              initialData={setupState.step4Data}
              userEmail={user?.email || ''}
              onSubmit={handleStep4Complete}
              onBack={() => goToStep(3)}
              loading={loading}
            />
          )}

          {setupState.currentStep === 5 && (
            <Step5Summary
              step1Data={setupState.step1Data}
              step2Data={setupState.step2Data}
              step3Data={setupState.step3Data}
              step4Data={setupState.step4Data}
              onSubmit={handleComplete}
              onBack={() => goToStep(4)}
              loading={loading}
            />
          )}
        </div>
      </div>
    </div>
  )
}
